import { NgIf, NgStyle } from '@angular/common';
import { Component, Injector, OnDestroy, OnInit, WritableSignal, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { PageHeaderService } from '../../layout/page-header/page-header.service';
import { IDateRangePickerDate } from '../../shared/components/date-range-picker/date-range-picker.interface';
import { FilterCardComponent } from '../../shared/components/filter-card/filter-card.component';
import { IDropdownOption } from '../../shared/components/mat-dropdown/mat-dropdown.component';
import { HttpUtilitiesService } from '../../utilities/http-utilities.service';
import { ItemsDatatableComponent } from './item-view/items-datatable/items-datatable.component';
import { ItemsKamishibaiComponent } from './item-view/items-kamishibai/items-kamishibai.component';
import { ItemsKanbanComponent } from './item-view/items-kanban/items-kanban.component';
import { IItemsFilterCardFields } from './items-filter-card/items-filter-card.interface';
import { ItemsFilterCardService, TItemsFilterCardManager } from './items-filter-card/items-filter-card.service';
import { ItemsKpiCardsComponent } from './items-kpi-cards/items-kpi-cards.component';
import { TKpiCardKey } from './items-kpi-cards/items-kpi-cards.interface';
import { ItemsViewPreferenceService } from './items-view-preference.service';
import { IBoardItemsInitialData, IBoardTreeStructure, TUrlItemScope } from './items.interface';
import { ItemsService } from './items.service';

@Component({
  imports: [
    FilterCardComponent,
    ItemsKpiCardsComponent,
    NgStyle,
    ItemsKanbanComponent,
    ItemsDatatableComponent,
    NgIf,
    ItemsKamishibaiComponent,
  ],
  providers: [HttpUtilitiesService, ItemsFilterCardService],
  selector: 'app-items',
  styleUrl: './items.component.scss',
  templateUrl: './items.component.html',
})
export class ItemsComponent implements OnInit, OnDestroy {
  public readonly filterCardData$ = new BehaviorSubject<Partial<IItemsFilterCardFields> | null>(null);
  public boardKey: WritableSignal<string | null> = signal<string | null>(null);
  public itemScope: WritableSignal<TUrlItemScope> = signal<TUrlItemScope>(null);
  public boardTreeStructure: IBoardTreeStructure[] = [];
  public initialBoard: (IDropdownOption & { key: string }) | undefined;
  public showFilterCard = PageHeaderService.isFilterCardVisible();
  public filterCardManager!: TItemsFilterCardManager;

  private readonly subscriptions: Subscription[] = [];
  private readonly injector = inject(Injector);

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly itemsService: ItemsService,
    private readonly translate: TranslateService,
    private readonly filterCardService: ItemsFilterCardService,
    private readonly itemsViewPreference: ItemsViewPreferenceService,
  ) {}

  public ngOnInit(): void {
    PageHeaderService.setPageHeader({
      showFilterButton: true,
    });
    this.subscriptions.push(
      this.activatedRoute.data.subscribe(({ boardItemsInitialData, filterCardManager }): void => {
        const initialData: IBoardItemsInitialData = boardItemsInitialData;
        this.boardTreeStructure = initialData?.boardTreeStructure;
        this.initialBoard = initialData?.initialBoard;
        this.filterCardManager = filterCardManager;

        if (initialData.item) {
          this.itemsService.openItemEditDialogAndSetUrl(
            initialData?.initialBoard?.key,
            initialData.item?.id,
            initialData.item?.key,
            this.translate.instant('button.saveChanges'),
          );
        }
      }),
      this.activatedRoute.paramMap.subscribe((paramMap: ParamMap): void => {
        this.boardKey.set(paramMap.get('key')! ?? null);
        this.itemScope.set((paramMap.get('scope') as TUrlItemScope) ?? null);
      }),

      PageHeaderService.filterCardVisibility$.subscribe((showFilterCard: boolean) => {
        this.showFilterCard = showFilterCard;
      }),
    );

    // Runs before app-filter-card's own ngOnInit (Angular always inits a parent before its
    // children), so any route-derived value must land in the manager's initialValue, not a live
    // patch — see ItemsFilterCardService.applyRouteContext / FilterCardManager.patchInitialValue.
    this.filterCardService.applyRouteContext(this.filterCardManager, this.initialBoard, this.itemScope());
    this.subscribeToFilterCard();

    if (this.initialBoard) {
      PageHeaderService.setPageHeader({
        icon: 'grid_view',
        subtitle: this.initialBoard.key,
        title: this.initialBoard.name,
      });
    }
  }

  public onKpiCardSelected(kpiCardKey: TKpiCardKey | null): void {
    this.filterCardService.applyKpiCardFilter(kpiCardKey, this.filterCardManager);
  }

  /**
   * `apply: true` is deliberate — unlike an ordinary chip commit (`onAdd`/`onChipEdit`/`onChipRemove`
   * in `FilterCardComponent`, none of which force-submit), navigating months is meant to apply
   * system-wide immediately (KPI cards, etc. should reflect the currently-viewed month too), not sit
   * pending behind an "Update" click. Trade-off, accepted as intentional: it also submits any *other*
   * chip the user has mid-edited but hasn't clicked "Update" for yet.
   * `ItemsKamishibaiComponent` additionally keeps its own calendar in sync locally
   * (`shiftDateRange`/`applyLocalDateRange`) rather than waiting on this round-trip.
   */
  public onKamishibaiDateRangeChange(dateRange: IDateRangePickerDate): void {
    this.filterCardManager.patchValue({ dateRange }, true);
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription?.unsubscribe());
  }

  private subscribeToFilterCard(): void {
    this.subscriptions.push(
      combineLatest({
        appliedValue: toObservable(this.filterCardManager.appliedValue, { injector: this.injector }).pipe(
          filter(Boolean),
        ),
        itemScope: toObservable(this.filterCardService.itemScope, { injector: this.injector }),
        itemView: toObservable(this.itemsViewPreference.itemView, { injector: this.injector }),
      }).subscribe(({ appliedValue, itemScope, itemView }) => {
        const filterCardData: Partial<IItemsFilterCardFields> = {
          ...(appliedValue as Partial<IItemsFilterCardFields>),
          itemScope,
          itemView,
        };

        this.filterCardService.updateUrlIfASingleBoardIsSelected(filterCardData);
        this.filterCardData$.next(filterCardData);
      }),
      this.filterCardManager.setAsDefault$.subscribe((value) =>
        this.filterCardService.persistAsDefault(value as Partial<IItemsFilterCardFields>),
      ),
    );
  }
}
